import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { ErrorBoundary } from "@sentry/react";
import { ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { Suspense, useEffect, useRef, useState } from "react";
import { useAISummaryQuery } from "../model/useAISummaryQuery";

const SKELETON_LINES: { id: string; width: string; paragraphStart: boolean }[] = [
  { id: "line-1", width: "w-full", paragraphStart: false },
  { id: "line-2", width: "w-full", paragraphStart: false },
  { id: "line-3", width: "w-4/5", paragraphStart: false },
  { id: "line-4", width: "w-full", paragraphStart: true },
  { id: "line-5", width: "w-3/5", paragraphStart: false },
];

function SparklesGradientDef() {
  return (
    <svg width={0} height={0} style={{ position: "absolute" }} aria-hidden="true">
      <defs>
        <linearGradient id="sparkles-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FF3BFF" />
          <stop offset="50%" stopColor="#ECBFBF" />
          <stop offset="100%" stopColor="#D94FD5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function AISummaryCardSkeleton() {
  return (
    <div className="mt-4 flex flex-col gap-2 md:mt-0">
      {SKELETON_LINES.map((line) => (
        <div
          key={line.id}
          className={`h-4 rounded-2 skeleton-shimmer ${line.width} ${line.paragraphStart ? "mt-2" : ""}`}
        />
      ))}
    </div>
  );
}

function AISummaryCardError() {
  return <p className="mt-4 text-body3 text-gray-500 md:mt-0">Failed to load AI summary.</p>;
}

const CONTENT_MAX_HEIGHT_PX = 150;

function AISummaryCardContent() {
  const { data } = useAISummaryQuery();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLParagraphElement>(null);
  const expandedHeightRef = useRef<number>(0);

  // AI 요약은 생성 비용이 드는 기능인데 지금까지 노출·사용 지표가 전혀 없어
  // 값어치를 판단할 근거가 없었다. 성공적으로 렌더된 경우만 한 건 남긴다.
  useEffect(
    function trackSummaryView() {
      EventLogger.view(CLIENT_EVENT.AI_SUMMARY_VIEW, { summary_length: data.summary.length });
    },
    [data.summary],
  );

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      expandedHeightRef.current = el.scrollHeight;
      setIsOverflowing(el.scrollHeight > CONTENT_MAX_HEIGHT_PX);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="mt-4 md:mt-0">
      <div
        className="relative overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{
          maxHeight: isExpanded ? expandedHeightRef.current : CONTENT_MAX_HEIGHT_PX,
        }}
      >
        <p ref={contentRef} className="text-body3 whitespace-pre-line">
          {data.summary}
        </p>
        {isOverflowing && !isExpanded && (
          <div className="pointer-events-none absolute right-0 bottom-0 left-0 h-10 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>
      {isOverflowing && (
        <button
          type="button"
          onClick={() => {
            EventLogger.click(CLIENT_EVENT.AI_SUMMARY_EXPAND, {
              is_expanded: !isExpanded,
              summary_length: data.summary.length,
            });
            setIsExpanded((prev) => !prev);
          }}
          className="mt-2 flex items-center gap-1 text-body3 text-gray-500 transition-colors hover:text-gray-700 ml-auto"
        >
          {isExpanded ? (
            <>
              접기
              <ChevronUp width={14} height={14} />
            </>
          ) : (
            <>
              더보기
              <ChevronDown width={14} height={14} />
            </>
          )}
        </button>
      )}
    </div>
  );
}

export function AISummaryCard() {
  return (
    // `.ai-summary-card`/`.ai-summary-card-inner`(index.css)는 모바일 전용 회전 그라디언트 테두리 트릭이라
    // md 이상에서는 `!` important 유틸로 덮어써 연보라 테두리 + 그라디언트 카드로 바꾼다.
    <div
      className="ai-summary-card shadow-box my-[2px]
        md:!animate-none md:![background:linear-gradient(180deg,#faf6fe,#fff_60%)] md:!p-0 md:overflow-hidden
        md:!rounded-8 md:border md:border-[#eddcfa]"
    >
      <div className="ai-summary-card-inner md:!p-0 md:!bg-transparent">
        <div className="flex gap-2 items-center md:px-4 md:py-3 md:border-b md:border-[#eddcfa]">
          <SparklesGradientDef />
          <Sparkles width={16} height={16} stroke="url(#sparkles-gradient)" />
          <h2 className="text-subtitle4 text-gray-1000">AI Summary</h2>
        </div>
        <div className="md:px-4 md:py-4">
          <ErrorBoundary fallback={<AISummaryCardError />}>
            <Suspense fallback={<AISummaryCardSkeleton />}>
              <AISummaryCardContent />
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}
